import { useState } from "react";
import { Plus, X } from "lucide-react";
import type { PricelistGridCellDto, PricelistGridDto } from "../../api/schemas";
import { makeCartItemGridKey } from "../../domain/quotation";

interface ProductGridProps {
  grid: PricelistGridDto | undefined;
  selectedItemKeys: Set<string>;
  onToggleCell: (cell: PricelistGridCellDto) => void;
}

export function ProductGrid({ grid, selectedItemKeys, onToggleCell }: ProductGridProps) {
  const [hoveredCell, setHoveredCell] = useState<
    { section: string | undefined; row: string; column: string } | undefined
  >();

  if (!grid) {
    return (
      <section className="panel product-grid-empty">
        <p>Select a brand, category, and pricelist to load products.</p>
      </section>
    );
  }

  const sections = buildGridSections(grid);

  return (
    <section className="panel product-grid-panel" aria-label="Product grid">
      <div className="section-heading">
        <div>
          <h2>{grid.category}</h2>
          <p>{grid.display_name}</p>
        </div>
      </div>

      <div className="grid-scroll">
        <div className="product-grid-content">
          {sections.map((section) => (
            <div className="product-grid-section" key={section.key}>
              {section.title && <h3>{section.title}</h3>}
              <table
                className={
                  section.columns.length <= 1
                    ? "product-grid product-grid-compact"
                    : "product-grid"
                }
              >
                <thead>
                  <tr>
                    <th scope="col">Size</th>
                    {section.columns.map((column) => (
                      <th
                        key={column}
                        scope="col"
                        className={
                          hoveredCell?.section === section.title &&
                          hoveredCell?.column === column
                            ? "grid-axis-highlight"
                            : undefined
                        }
                      >
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.rows.map((row) => (
                    <tr key={row}>
                      <th
                        scope="row"
                        className={
                          hoveredCell?.section === section.title && hoveredCell?.row === row
                            ? "grid-axis-highlight"
                            : undefined
                        }
                      >
                        {row}
                      </th>
                      {section.columns.map((column) => {
                        const cell = section.cellsByPosition.get(`${row}::${column}`);
                        const isSelected = cell
                          ? selectedItemKeys.has(makeCartItemGridKey(cell))
                          : false;
                        return (
                          <td key={column}>
                            {cell ? (
                              <button
                                type="button"
                                className={
                                  isSelected
                                    ? "grid-cell-button grid-cell-button-selected"
                                    : "grid-cell-button"
                                }
                                onClick={() => onToggleCell(cell)}
                                onMouseEnter={() =>
                                  setHoveredCell({ section: section.title, row, column })
                                }
                                onMouseLeave={() => setHoveredCell(undefined)}
                                onFocus={() =>
                                  setHoveredCell({ section: section.title, row, column })
                                }
                                onBlur={() => setHoveredCell(undefined)}
                                title={`${isSelected ? "Remove" : "Add"} ${cell.label}`}
                              >
                                <span>{cell.price.toLocaleString("en-IN")}</span>
                                {isSelected ? (
                                  <X aria-hidden="true" size={15} />
                                ) : (
                                  <Plus aria-hidden="true" size={15} />
                                )}
                              </button>
                            ) : (
                              <span className="empty-cell">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

interface GridSection {
  key: string;
  title: string | undefined;
  rows: string[];
  columns: string[];
  cellsByPosition: Map<string, PricelistGridCellDto>;
}

function buildGridSections(grid: PricelistGridDto): GridSection[] {
  const sectionTitles = Array.from(
    new Set(grid.cells.map((cell) => cell.section).filter((section) => section))
  );

  if (sectionTitles.length === 0) {
    return [buildGridSection(grid, undefined)];
  }

  return sectionTitles.map((section) => buildGridSection(grid, section));
}

function buildGridSection(
  grid: PricelistGridDto,
  section: string | undefined
): GridSection {
  const cells = grid.cells.filter((cell) => cell.section === section);
  const rowSet = new Set(cells.map((cell) => cell.row));
  const columnSet = new Set(cells.map((cell) => cell.column));

  return {
    key: section ?? "default",
    title: section,
    rows: grid.rows.filter((row) => rowSet.has(row)),
    columns: grid.columns.filter((column) => columnSet.has(column)),
    cellsByPosition: new Map(cells.map((cell) => [`${cell.row}::${cell.column}`, cell]))
  };
}
